/**
 * @author Srijan Shakya
 * @description https://marvelapp.com/prototype/c4fj1hh/screen/74507425
 */

import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import find from 'ramda/src/find';
import filter from 'ramda/src/filter';
import isNil from 'ramda/src/isNil';
import isEmpty from 'ramda/src/isEmpty';
import length from 'ramda/src/length';
import propEq from 'ramda/src/propEq';
import values from 'ramda/src/values';
import moment from 'moment';
import ResponsiveTabs from '@components/@core/ResponsiveTabs';
import CareerCertificationHeader from '@components/CareerCertificationHeader';
import * as Instances from '@components/CareerRequiredInstances';
import Settings from '@components/@core/Settings';
import TrainingCourses from '~/views/Manage/Reports/TrainingCourses';
import ProgressBar from '@components/@core/ProgressBar';
import { Expirations } from './Config';
import './Detail.scss';

const QuotaHeader = () => (
    <div className="career-required-instances">
        <div className="quotas pt-4">
            <p className="dsl-b18 text-500 mb-3">Goals</p>
            <div className="list py-2 px-1 desktop-screen border-bottom">
                <div className="d-flex-6">
                    <div className="dsl-m12">Goal</div>
                </div>
                <div className="d-flex-3">
                    <div className="dsl-m12">Calculation</div>
                </div>
                <div className="d-flex-3">
                    <div className="dsl-m12">Greater/Less Than</div>
                </div>
                <div className="d-flex-1">
                    <div className="dsl-m12">Target</div>
                </div>
                <div className="d-flex-1">
                    <div className="dsl-m12">Actual</div>
                </div>
                <div className="d-flex-2">
                    <div className="dsl-m12">Status</div>
                </div>
            </div>
        </div>
    </div>
);

class Detail extends Component {
    state = { active: 0 };

    static getDerivedStateFromProps(nextProps, prevState) {
        const { data, jobs } = nextProps;
        if (!isNil(data) && !isNil(data.data) && data !== prevState.data) {
            const job = find(propEq('id', data.job_role_id), jobs) || {};
            return { job };
        }
        return null;
    }

    handleContentPagination = (e) => {
        this.setState({ curLevel: e - 1 });
    };

    handleDocumentName = (document) => {
        let filename = document?.split('%2F');
        if (filename.length > 1) {
            filename.splice(0, 2);
            return filename;
        } else {
            return document;
        }
    };

    render() {
        const { active } = this.state;
        const { userRole, data, onSelect, authors, competencies, categories, departments } = this.props;

        if (isNil(data) || isEmpty(data)) return null;

        const isLevel = !isNil(data.data.levels) && !isEmpty(data.data.levels);
        const levelData = isLevel ? values(data.data.levels) : [];
        const levels = values(data?.data?.levels) || [];

        const completedQuotas = filter((e) => !isNil(e.completed_at), levels[active].quotas ?? []);
        const progress = length(levels[active].quotas) > 0 ? ((completedQuotas.length / length(levels[active].quotas)) * 100).toFixed(2) : 100;
        const settings = [
            { name: 'Author', id: [data.author_id], options: authors },
            { name: 'Departments', id: data.data.department, options: departments },
            { name: 'Competencies', id: data.data.compentency, options: competencies },
            { name: 'Categories', id: data.data.category, options: categories },
        ];
        const stats = [
            { name: 'Created', value: moment(data?.created_at).format('MMM DD, YY') || null },
            { name: 'Created By', value: data?.created_by || 'Na' },
            { name: 'Last Edited', value: moment(data?.updated_at).format('MMM DD, YY') || null },
            { name: 'Last edits by', value: data?.edited_by || 'Na' },
            { name: 'Assigned', value: data?.assigned || 'Na' },
        ];

        return (
            <div className="lib-programs-certification overflow-hidden">
                <CareerCertificationHeader program={data} type="certifications" levelData={levelData} userRole={userRole} current={active} onSelect={onSelect} />
                <div className="lib-programs-career">
                    <div className="career-content card-header mb-0">
                        <p className="dsl-b22 bold mb-3">Requirements</p>
                        <ResponsiveTabs
                            className="mb-3"
                            active={`${active}`}
                            data={levels?.map((level, index) => ({
                                id: `${index}`,
                                name: level?.title?.length > 15 ? `${level.title?.substring(0, 15)}...` : level?.title || 'NA',
                            }))}
                            onTab={(e) => this.setState({ active: e.id })}
                        />
                        <QuotaHeader />
                        {length(levels[active].quotas) > 0 ? (
                            <Instances.QuotasDetail data={levels[active].quotas} stat type="instance" programType="certificate" />
                        ) : (
                            <p className="dsl-b16 py-4 mb-0 border-bottom">No Quotas Assigned</p>
                        )}
                        <div className="d-flex ai-center jc-end mt-4">
                            <span className="dsl-b16 bold">Goals completed</span>
                            <span className="dsl-b16 mx-3">{length(levels[active].quotas)}</span>
                            <ProgressBar value={progress} />
                        </div>
                        <TrainingCourses data={levels[active]?.trainings?.items} />
                        <div className="pt-5">
                            <p className="dsl-b18 text-500">Expiration</p>
                            <div className="d-flex ai-center py-3">
                                <span className="dsl-m14 me-5">Expiration</span>
                                <span className="dsl-b16">{Expirations[levels[active]?.expired_at || 0].name}</span>
                            </div>
                        </div>
                    </div>
                    <Settings values={settings} stats={stats} />
                </div>
            </div>
        );
    }
}

Detail.propTypes = {
    userRole: PropTypes.number,
    data: PropTypes.shape({
        id: PropTypes.number,
        title: PropTypes.string,
        data: PropTypes.any,
    }),
    authors: PropTypes.array.isRequired,
    departments: PropTypes.array.isRequired,
    competencies: PropTypes.array.isRequired,
    categories: PropTypes.array.isRequired,
    onSelect: PropTypes.func,
};

Detail.defaultProps = {
    userRole: 1,
    data: { id: 0, title: '', data: {} },
    authors: [],
    departments: [],
    competencies: [],
    categories: [],
    onSelect: () => {},
};

export default Detail;
